import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, ExternalLink, MoveUp, MoveDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import ImageUploader from '../components/ImageUploader';
import GalleryUploader from '../components/GalleryUploader';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPortfolio = () => {
  const { isAdmin, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  
  const [formData, setFormData] = useState({
    clientName: '',
    year: new Date().getFullYear().toString(),
    description: '',
    servicesPerformed: '',
    website: '',
    socialLinks: '',
    coverImage: '',
    galleryImages: [],
    featured: false,
    order: 0
  });

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) {
      navigate('/login');
      return;
    }
    fetchClients();
  }, [isAuthenticated, isAdmin]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast({
        title: "Error",
        description: "Failed to fetch clients",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCoverUpload = async (file) => {
    if (!file) {
      setFormData({ ...formData, coverImage: '' });
      return;
    }

    setUploadingCover(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('cover', file);

      const response = await axios.post(`${API}/upload/cover`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setFormData({ ...formData, coverImage: response.data.url });
      toast({
        title: "Success",
        description: "Cover image uploaded successfully"
      });
    } catch (error) {
      console.error('Cover upload error:', error);
      toast({
        title: "Error",
        description: "Failed to upload cover image",
        variant: "destructive"
      });
    } finally {
      setUploadingCover(false);
    }
  };

  const handleGalleryUpload = async (images) => {
    const newFiles = images.filter(img => img.isNew && img.file);
    const existingUrls = images.filter(img => !img.isNew && typeof img === 'string');

    if (newFiles.length === 0) {
      setFormData({ ...formData, galleryImages: existingUrls });
      return;
    }

    setUploadingGallery(true);
    try {
      const formDataUpload = new FormData();
      newFiles.forEach(img => {
        formDataUpload.append('gallery', img.file);
      });

      const response = await axios.post(`${API}/upload/gallery`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const uploadedUrls = response.data.images.map(img => img.url);
      const allImages = [...existingUrls, ...uploadedUrls];

      setFormData({ ...formData, galleryImages: allImages });
      toast({
        title: "Success",
        description: `${uploadedUrls.length} image(s) uploaded successfully`
      });
    } catch (error) {
      console.error('Gallery upload error:', error);
      toast({
        title: "Error",
        description: "Failed to upload gallery images",
        variant: "destructive"
      });
    } finally {
      setUploadingGallery(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.coverImage) {
      toast({
        title: "Error",
        description: "Please upload a cover image",
        variant: "destructive"
      });
      return;
    }

    const clientData = {
      clientName: formData.clientName,
      slug: formData.clientName.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      year: formData.year,
      description: formData.description,
      servicesPerformed: formData.servicesPerformed.split(',').map(s => s.trim()).filter(Boolean),
      website: formData.website,
      socialLinks: formData.socialLinks ? formData.socialLinks.split('\n').map(line => {
        const [platform, url] = line.split(':').map(s => s.trim());
        return { platform, url };
      }).filter(link => link.platform && link.url) : [],
      coverImage: formData.coverImage,
      galleryImages: formData.galleryImages,
      featured: formData.featured,
      order: parseInt(formData.order) || 0
    };

    try {
      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient._id}`, clientData);
        toast({
          title: "Success",
          description: "Client updated successfully"
        });
      } else {
        await axios.post(`${API}/clients`, clientData);
        toast({
          title: "Success",
          description: "Client created successfully"
        });
      }
      
      setDialogOpen(false);
      resetForm();
      fetchClients();
    } catch (error) {
      console.error('Error saving client:', error);
      toast({
        title: "Error",
        description: "Failed to save client",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      clientName: client.clientName,
      year: client.year,
      description: client.description,
      servicesPerformed: client.servicesPerformed.join(', '),
      website: client.website || '',
      socialLinks: client.socialLinks.map(link => `${link.platform}: ${link.url}`).join('\n'),
      coverImage: client.coverImage,
      galleryImages: client.galleryImages || [],
      featured: client.featured || false,
      order: client.order || 0
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    
    try {
      await axios.delete(`${API}/clients/${id}`);
      toast({
        title: "Success",
        description: "Client deleted successfully"
      });
      fetchClients();
    } catch (error) {
      console.error('Error deleting client:', error);
      toast({
        title: "Error",
        description: "Failed to delete client",
        variant: "destructive"
      });
    }
  };

  const handleReorder = async (id, newOrder) => {
    try {
      await axios.put(`${API}/clients/${id}/reorder`, { order: newOrder });
      fetchClients();
    } catch (error) {
      console.error('Error reordering client:', error);
    }
  };

  const resetForm = () => {
    setEditingClient(null);
    setFormData({
      clientName: '',
      year: new Date().getFullYear().toString(),
      description: '',
      servicesPerformed: '',
      website: '',
      socialLinks: '',
      coverImage: '',
      galleryImages: [],
      featured: false,
      order: 0
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F4B400] mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-white pt-24 pb-16">
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">Portfolio Management</h1>
            <p className="text-gray-400">Manage client case studies</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={resetForm}
                className="bg-[#F4B400] text-black hover:bg-[#d49f00]"
              >
                <Plus className="mr-2" size={18} />
                Add Client
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white text-black">
              <DialogHeader>
                <DialogTitle>{editingClient ? 'Edit Client' : 'Add New Client'}</DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Client Name *</Label>
                  <Input
                    value={formData.clientName}
                    onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                    required
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label>Year *</Label>
                  <Input
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                    required
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label>Description *</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    rows={4}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label>Services Performed (comma-separated) *</Label>
                  <Textarea
                    value={formData.servicesPerformed}
                    onChange={(e) => setFormData({ ...formData, servicesPerformed: e.target.value })}
                    required
                    rows={3}
                    placeholder="Business Cards, Website Design, Vehicle Wrap"
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label>Website URL</Label>
                  <Input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://example.com"
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label>Social Links (one per line: Platform: URL)</Label>
                  <Textarea
                    value={formData.socialLinks}
                    onChange={(e) => setFormData({ ...formData, socialLinks: e.target.value })}
                    rows={4}
                    placeholder="Facebook: https://facebook.com/page&#10;Instagram: https://instagram.com/profile"
                    className="mt-2"
                  />
                </div>

                <div>
                  <ImageUploader
                    label="Cover Image *"
                    currentImage={formData.coverImage}
                    onUpload={handleCoverUpload}
                  />
                </div>

                <div>
                  <GalleryUploader
                    images={formData.galleryImages}
                    onImagesChange={handleGalleryUpload}
                  />
                </div>

                <div>
                  <Label>Display Order</Label>
                  <Input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                    className="mt-2"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                    className="h-4 w-4"
                  />
                  <Label>Featured Client</Label>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="flex-1 bg-[#F4B400] text-black hover:bg-[#d49f00]">
                    {editingClient ? 'Update Client' : 'Create Client'}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setDialogOpen(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Clients List */}
        <div className="grid grid-cols-1 gap-4">
          {clients.map((client, index) => (
            <Card key={client._id} className="bg-white text-black">
              <CardContent className="p-6">
                <div className="flex gap-6">
                  <img 
                    src={client.coverImage} 
                    alt={client.clientName}
                    className="w-32 h-32 object-cover rounded-lg"
                  />
                  
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="text-2xl font-bold">{client.clientName}</h3>
                        <Badge className="mt-2 bg-[#F4B400] text-black">{client.year}</Badge>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReorder(client._id, client.order - 1)}
                          disabled={index === 0}
                        >
                          <MoveUp size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReorder(client._id, client.order + 1)}
                          disabled={index === clients.length - 1}
                        >
                          <MoveDown size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(client)}
                        >
                          <Edit size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(client._id)}
                          className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                    
                    <p className="text-gray-600 mb-3 line-clamp-2">{client.description}</p>
                    
                    <div className="flex flex-wrap gap-2 mb-3">
                      {client.servicesPerformed.slice(0, 5).map((service, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {service}
                        </Badge>
                      ))}
                      {client.servicesPerformed.length > 5 && (
                        <Badge variant="outline" className="text-xs text-[#F4B400] border-[#F4B400]">
                          +{client.servicesPerformed.length - 5}
                        </Badge>
                      )}
                    </div>
                    
                    <div className="flex gap-3">
                      {client.website && (
                        <a 
                          href={client.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-sm text-[#F4B400] hover:underline"
                        >
                          Website <ExternalLink size={12} />
                        </a>
                      )}
                      {client.socialLinks.length > 0 && (
                        <span className="text-sm text-gray-500">
                          {client.socialLinks.length} social links
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {clients.length === 0 && (
          <div className="text-center py-16">
            <p className="text-gray-400 text-lg mb-4">No clients yet</p>
            <Button onClick={() => setDialogOpen(true)} className="bg-[#F4B400] text-black hover:bg-[#d49f00]">
              <Plus className="mr-2" size={18} />
              Add Your First Client
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPortfolio;
